import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import {
  IColumn,
  IDetailsHeaderProps,
  IRenderFunction,
  PrimaryButton,
  SelectionMode,
  Stack,
  Sticky,
  StickyPositionType,
  Text,
  TooltipHost,
} from '@fluentui/react';
import { ActionMessageModal } from 'common/components/ActionMessageModal';
import { ActiveLink } from 'common/components/ActiveRowLink';
import { showCustomToast } from 'common/components/CustomToast/utils';
import { InfiniteList } from 'common/components/InfiniteList';
import { TABLE_ROWS } from 'common/constants';
import { useCommonStyles } from 'common/styles';
import { EntityDeleteInput } from 'common/types/globalTypes';
import { EntityType } from 'common/types/utility';
import { OrderDirection, SortOrder } from 'common/utils/commonTypes';
import React, { useState } from 'react';
import { useHistory } from 'react-router';

import { toOrderByVariable } from '../utils';
import {
  DocumentPoolDelete,
  DocumentPoolDeleteVariables,
} from './__generated__/DeleteDocumentPool';
import {
  DocumentPools,
  DocumentPools_documentPools_nodes,
  DocumentPoolsVariables,
} from './__generated__/DocumentPools';
import { getColumns } from './column.data';
import DELETE_DOCUMENT_POOL from './DeleteDocumentPool.graphql';
import DOCUMENTPOOL from './DocumentPools.graphql';

import { HelpIcon } from 'common/trainingSystem/HelpIcon';
import { useStyles } from './index.styles';
type DocumentPoolType = DocumentPools_documentPools_nodes;
export const DocumentsPool: React.FC = () => {
  const commonStyles = useCommonStyles();
  const history = useHistory();
  const styles = useStyles();
  const [sortOrderParam, setSortOrderParam] = useState<SortOrder>();
  const [selectedList, setSelectedList] = useState<DocumentPoolType[]>([]);
  const sortColumns = (columnName: string, direction: string) => {
    let orderBy = {
      column: columnName,
      direction: direction as OrderDirection,
    };
    setSortOrderParam(orderBy);
    handleSortAndPagination(orderBy);
  };

  // ONCLICK FUNCTION ON HEADER TO HANDLE SORT
  const _onColumnClick = (column: IColumn) => {
    const newColumns: IColumn[] = _columns.slice();
    const currColumn: IColumn = newColumns.filter(
      (currCol) => column.key === currCol.key
    )[0];
    newColumns.forEach((newCol: IColumn) => {
      if (newCol === currColumn) {
        currColumn.isSortedDescending = !currColumn.isSortedDescending;
        currColumn.isSorted = true;
      } else {
        newCol.isSorted = false;
      }
    });
    setGridColumns(newColumns);
    let direction = 'asc';
    if (column.isSortedDescending === true) {
      direction = 'asc';
      sortColumns(column.key, direction);
    } else {
      direction = 'desc';
      sortColumns(column.key, direction);
    }
  };

  //FOR RENDERING THE COLUMN CELL VALUES IN DESIRED FORMAT.
  let _renderItemColumn = (
    item: DocumentPoolType,
    _index: number | undefined,
    column: IColumn | undefined
  ) => {
    switch (column?.key) {
      case 'name':
        return (
          <Stack verticalAlign="center" className={styles.cellContainer}>
            <ActiveLink to={`/doc/groups/group/${item.id}`}>
              {item.name}
            </ActiveLink>
          </Stack>
        )
      case 'description':
        return (
          <Stack verticalAlign="center" className={styles.cellContainer}>
            <Text>{item.description && item.description}</Text>
          </Stack>
        )
      case 'defaultDirectoryTypes.description':
        return (
          <Stack verticalAlign="center" className={styles.cellContainer}>
            <Text>{item.defaultDirectoryTypes?.description}</Text>
          </Stack>
        )
      case 'defaultDocumentTypes.documentType':
        return (<Stack verticalAlign="center" className={styles.cellContainer}>
          <Text>{item.defaultDocumentTypes?.documentType}</Text>
        </Stack>
        )
      case 'documentPoolTotals.availableDocuments':
        return (
          <Stack verticalAlign="center" className={styles.cellContainer}>
            <TooltipHost
              content={`${item._documentFileCount?.selectedDocuments} Attached`}
            >
              <Text>{item._documentFileCount?.availableDocuments}</Text>
            </TooltipHost>
          </Stack>
        )
    }
  };
  let _columns = getColumns(_onColumnClick);
  const [gridColumns, setGridColumns] = useState<IColumn[]>(_columns);

  const {
    loading: documentPoolLoading,
    data: documentPoolListData,
    fetchMore,
    refetch,
    variables,
    networkStatus,
  } = useQuery<DocumentPools, DocumentPoolsVariables>(DOCUMENTPOOL, {
    variables: {
      first: TABLE_ROWS,
      orderBy: ['NAME_ASC'],
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  const refetching =
    networkStatus === NetworkStatus.refetch ||
    networkStatus === NetworkStatus.setVariables;

  const handleSortAndPagination = (
    sortOrder?: SortOrder,
    showMore?: boolean
  ) => {
    const variables: DocumentPoolsVariables = {
      first: TABLE_ROWS,
      after: showMore
        ? documentPoolListData?.documentPools?.pageInfo.endCursor
        : undefined,
      orderBy: toOrderByVariable(sortOrder),
    };
    if (showMore) fetchMore({ variables });
    else refetch(variables);
  };

  const [documentPoolDelete] = useMutation<
    DocumentPoolDelete,
    DocumentPoolDeleteVariables
  >(DELETE_DOCUMENT_POOL, { errorPolicy: 'all' });

  const onRenderDetailsHeader: IRenderFunction<IDetailsHeaderProps> = (
    props,
    defaultRender
  ) => {
    if (!props) return null;
    return (
      <Sticky stickyPosition={StickyPositionType.Header} isScrollSynced>
        <Stack>
          {defaultRender!({
            ...props,
          })}
        </Stack>
      </Sticky>
    );
  };

  const _onConfirm = async () => {
    let deletableData: EntityDeleteInput[] = [];
    for (let data of selectedList) {
      if (data._isDeletable) {
        deletableData.push({
          id: data.id,
          rowTimestamp: data._rowTimestamp!,
        });
      }
    }

    if (deletableData.length > 0) {
      const { errors } = await documentPoolDelete({
        variables: {
          input: {
            entityDelete: deletableData,
          },
        },
        update: (cache, { data }) => {
          const deletedIds = data?.documentPoolDelete?.deletedEntities?.map(
            (entity) => entity?.id
          );
          if (deletedIds) {
            const filteredList =
              documentPoolListData?.documentPools?.nodes.filter(
                (doc) => deletedIds.indexOf(doc.id) === -1
              );
            const newData: DocumentPools = {
              documentPools: {
                pageInfo: documentPoolListData?.documentPools?.pageInfo!,
                nodes: filteredList!,
                totalCount:
                  documentPoolListData?.documentPools?.totalCount! -
                  deletedIds.length,
              },
              // currentUserProfile: documentPoolListData?.currentUserProfile!
            };
            cache.writeQuery<DocumentPools, DocumentPoolsVariables>({
              query: DOCUMENTPOOL,
              data: newData,
              variables: variables,
            });
          }
        },
      });

      if (errors?.length)
        showCustomToast({
          content: errors[0].message,
          appearance: 'error',
        });
      else
        showCustomToast({
          content: 'Records deleted successfully',
          appearance: 'success',
        });
    } else
      showCustomToast({
        content: 'Cannot delete these records.',
        appearance: 'error',
      });
  };

  return (
    <>
      <Sticky stickyPosition={StickyPositionType.Header}>
        <Stack className={commonStyles.listHeaderContainer}>
          <Stack
            horizontal
            horizontalAlign="space-between"
            verticalAlign="center"
            className={commonStyles.listTitleContainer}
          >
            <Stack
              horizontal
              verticalAlign="center"
              tokens={{ childrenGap: 7 }}
            >
              <Text variant="xLarge">Document Folders</Text>
              <HelpIcon appLinkType={'DOCUMENT_POOL'} />
            </Stack>
            <Stack horizontal tokens={{ childrenGap: 10 }}>
              {selectedList.length > 0 && (
                <ActionMessageModal
                  entityType={EntityType.Folder}
                  disabled={
                    !selectedList.some((selected) => selected._isDeletable)
                  }
                  visible={selectedList.length > 0}
                  multiple={{
                    validCount: selectedList.filter(
                      (selected) => selected._isDeletable
                    ).length,
                    invalidNames: selectedList
                      .filter((selected) => !selected._isDeletable)
                      .map((cannotDelete) => cannotDelete.name),
                  }}
                  onConfirm={_onConfirm}
                />
              )}

              <PrimaryButton
                onClick={() => history.push('/doc/groups/group')}
                iconProps={{
                  iconName: 'Add',
                }}
                text="New Folder"
              />
            </Stack>
          </Stack>
        </Stack>
      </Sticky>

      <InfiniteList
        onRenderDetailsHeader={onRenderDetailsHeader}
        items={
          !refetching
            ? documentPoolListData?.documentPools?.nodes || []
            : undefined
        }
        loading={documentPoolLoading}
        hasNextPage={
          documentPoolListData?.documentPools?.pageInfo?.hasNextPage!
        }
        selectionMode={SelectionMode.multiple}
        columns={gridColumns}
        onRenderItemColumn={_renderItemColumn}
        onLoadMore={() => handleSortAndPagination(sortOrderParam, true)}
        onSelectionChanged={setSelectedList}
      />
    </>
  );
};
